/**
 * CR-7782: the Call Off Management list's columns for this viewer — which
 * show, and in what order.
 *
 * CR-7732 gave the haulier a show/hide picker. Every role now has one, and it
 * reorders as well, because what matters varies: a customer chasing costs
 * wants the haulage figures up front, a supplier planning its dock wants the
 * pickup dates. The columns themselves, and who is offered which, live in
 * `callOffColumnConfig`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type CallOffColumn,
  type CallOffColumnLayout,
  type CallOffListRole,
  columnsForRole,
  defaultLayout,
  loadLayout,
  saveLayout,
} from './callOffColumnConfig';

/**
 * The call-off list's column layout, remembered per user and role.
 *
 * @param role - whose list it is; decides the columns offered and their defaults
 * @param userId - the signed-in user, so a shared browser keeps layouts apart
 * @returns every offered column in the viewer's order, the visible ones, and
 *   the toggle / move / show-all / reset actions
 *
 * @example
 * const { visibleColumns, toggle, move } = useCallOffColumns('customer', user?.id);
 * {visibleColumns.map(column => <TableHead key={column.id}>{column.label}</TableHead>)}
 */
export function useCallOffColumns(role: CallOffListRole, userId?: number | string) {
  const storeKey = `${role}:${userId ?? ''}`;
  // `changed`: the viewer edited this layout, so it is worth remembering. A
  // layout only ever loaded is not written back.
  const [state, setState] = useState(() => ({
    key: storeKey, role, userId, layout: loadLayout(role, userId), changed: false,
  }));

  // The role can settle after first render (the haulier flag arrives with the
  // menu settings), so reload rather than write one role's layout to another.
  useEffect(() => {
    if (state.key !== storeKey) {
      setState({ key: storeKey, role, userId, layout: loadLayout(role, userId), changed: false });
    }
  }, [state.key, storeKey, role, userId]);

  // Saved under the role and user the layout was loaded for, never the
  // incoming ones mid-switch.
  useEffect(() => {
    if (state.changed) saveLayout(state.role, state.userId, state.layout);
  }, [state]);

  const columns = useMemo(() => columnsForRole(role), [role]);
  const { layout } = state;

  const update = useCallback((next: (prev: CallOffColumnLayout) => CallOffColumnLayout) => {
    setState((prev) => ({ ...prev, layout: next(prev.layout), changed: true }));
  }, []);

  const orderedColumns = useMemo(() => {
    const byId = new Map(columns.map((c) => [c.id, c]));
    return layout.order
      .map((id) => byId.get(id))
      .filter((c): c is CallOffColumn => c !== undefined);
  }, [columns, layout.order]);

  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => !layout.hidden.includes(c.id)),
    [orderedColumns, layout.hidden],
  );

  const toggle = useCallback((id: string) => {
    if (columns.find((c) => c.id === id)?.fixed) return;
    update((prev) => ({
      ...prev,
      hidden: prev.hidden.includes(id) ? prev.hidden.filter((h) => h !== id) : [...prev.hidden, id],
    }));
  }, [columns, update]);

  /** Move the column at `from` to `to` (indexes into `orderedColumns`). */
  const move = useCallback((from: number, to: number) => {
    update((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.order.length || to >= prev.order.length) {
        return prev;
      }
      const order = [...prev.order];
      const [moved] = order.splice(from, 1);
      order.splice(to, 0, moved);
      return { ...prev, order };
    });
  }, [update]);

  const showAll = useCallback(() => update((prev) => ({ ...prev, hidden: [] })), [update]);
  const reset = useCallback(() => update(() => defaultLayout(role)), [role, update]);

  /** Whether a column is offered to this role and showing. */
  const isVisible = useCallback(
    (id: string) => visibleColumns.some((c) => c.id === id),
    [visibleColumns],
  );

  return {
    orderedColumns,
    visibleColumns,
    isVisible,
    toggle,
    move,
    showAll,
    reset,
    hiddenCount: orderedColumns.length - visibleColumns.length,
  };
}
